import { ref, type Ref } from 'vue';
import { useFormStore } from '../form/formStore';
import { RoadsideAssistanceService as _roadsideAssistanceService } from '@/services/store/roadside-assistance.service';

export function useRoadsideAssistanceBase<
  T extends {
    registrationNumber: string;
    phoneNumber: string;
    breakdownLocationLatitude: number;
    breakdownLocationLongitude: number;
    breakdownLocationAddress: string;
    breakdownCause: string;
    comments: string;
    vehicle: any;
  },
>(initialDetails: T) {
  const formStore = useFormStore();

  const requestRoadsideAssistanceDetails = ref<T>(initialDetails);

  const isVehicleDetailsFromPortfolio = ref<boolean>(false);

  const rsaTrackingId = ref<string | null>(null);
  const dispatchGroup = ref<string | null>(null);

  function setVehicleDetails(buildYear: number, make: string, model: string, colour: string) {
    requestRoadsideAssistanceDetails.value.vehicle = { buildYear, make, model, colour };
    isVehicleDetailsFromPortfolio.value = false;
  }

  function setBreakdownLocation(latitude: number, longitude: number, formattedAddress: string) {
    requestRoadsideAssistanceDetails.value.breakdownLocationLatitude = latitude;
    requestRoadsideAssistanceDetails.value.breakdownLocationLongitude = longitude;
    requestRoadsideAssistanceDetails.value.breakdownLocationAddress = formattedAddress;
  }

  function setComments(comments: string) {
    requestRoadsideAssistanceDetails.value.comments = comments;
  }

  function clearComments() {
    requestRoadsideAssistanceDetails.value.comments = '';
  }

  function clearBreakdownCause() {
    requestRoadsideAssistanceDetails.value.breakdownCause = '';
  }

  /*
   * Add/ remove steps
   */
  async function checkAndAdjustStep(
    stepId: string,
    addStep: boolean = false,
    indexToAddAfter: number | undefined = undefined
  ) {
    const step = formStore.getStep(stepId);
    if (step && !addStep) {
      await formStore.removeStep(step.stepIndex!);
    } else if (!step && addStep && indexToAddAfter) {
      formStore.addStepToScope(stepId, {
        removable: true,
        afterIndex: indexToAddAfter,
        withOrdinalNumber: false,
      });
    }
  }

  const isTrackable = async () => {
    return await _roadsideAssistanceService.isTrackable(dispatchGroup.value!);
  };

  return {
    requestRoadsideAssistanceDetails: requestRoadsideAssistanceDetails as Ref<T>,
    isVehicleDetailsFromPortfolio,
    rsaTrackingId,
    dispatchGroup,
    setVehicleDetails,
    setBreakdownLocation,
    setComments,
    clearComments,
    clearBreakdownCause,
    checkAndAdjustStep,
    isTrackable,
  };
}
